/**
 * WHERE THE MONEY COMES FROM, ACCOUNT BY ACCOUNT, AND WHY IN THAT ORDER (owner, 18 Oct 2026: "I want it
 * clearly stated when the bank account runs out and then which asset and so on … when will the house
 * come into play … if you had two ABPs do we explain which one first and why … shares last because we
 * pay CGT").
 *
 * The order is the projection engine's own, read from its plugins (server/src/rest/calculation/plugins):
 *   bank accounts, in the order listed (order 10) → account-based pensions, in the order listed (11)
 *   → accumulation super from age 60 (11, after pensions) → shares, last, because a sale realises CGT
 *   and must cover its own tax (12) → the care fees drawn from the refundable deposit (13) → borrowing
 *   against the home (14).
 * The years come from the projection rows: each account's balance per year, so "runs low around year 4"
 * is the engine's figure, not a guess. An account the plan itself emptied (drawn for the deposit) is
 * named as such and left out of the order (see moneySequence.poolsOf).
 *
 * Two shapes from one derivation: a short summary for the Explain this line, and the full story behind
 * "see the order, and why" — so the card stays light and the detail is there for whoever wants it.
 */
import React, { useState } from 'react';
import { planRunwayCopy, summariseFunding, type PlanRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { formatCurrency } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { INVESTMENT_PROFILES } from '../../../../shared/src/calculation/investmentProfiles.ts';

export interface StoryRow {
    year?: number;
    assets?: Record<string, Array<{ value?: unknown }> | undefined>;
}

type PoolKey = 'bankAccounts' | 'accountBasedPensions' | 'superannuation' | 'shares';
const POOL_ORDER: PoolKey[] = ['bankAccounts', 'accountBasedPensions', 'superannuation', 'shares'];

export interface Account {
    key: PoolKey;
    name: string;
    /** Balance with the plan in place (the first projected year), before any shortfall is drawn. */
    opening: number;
    /** What it was on the file, before the plan. */
    before: number;
    /**
     * A pension's assumed return a year, after fees, and where it came from: the fund's own figure on the
     * file, or our Balanced assumption when none was entered (the rate the engine grows it at and ranks
     * the pensions by — owner, 21 Oct 2026: the client must be comfortable with the rates used).
     */
    rate?: { pct: number; source: 'file' | 'assumed' };
    /** A pension started before 1 January 2015: Centrelink's older income-test treatment, so drawn last. */
    pre2015?: boolean;
    /** The projected year it falls below $1,000; null if it lasts the rows out. */
    lowYear: number | null;
    /** Emptied by the plan itself (the deposit), not by the shortfall. */
    spentByPlan: boolean;
}

const num = (v: unknown) => parseFloat(String(v ?? 0)) || 0;

const GROUP_WORDS: Record<PoolKey, { one: string; many: string }> = {
    bankAccounts: { one: 'the everyday account', many: 'the everyday accounts' },
    accountBasedPensions: { one: 'the account-based pension', many: 'the account-based pensions' },
    superannuation: { one: 'the super', many: 'the super' },
    shares: { one: 'the shares', many: 'the shares' },
};

/** Why the engine reaches for this kind of money at this point in the order. */
const WHY: Record<PoolKey, string> = {
    bankAccounts: 'Cash goes first: there is nothing to sell, nothing to apply for, and no tax on taking it out.',
    accountBasedPensions: 'A pension’s regular payments are already counted as income above; drawing more is simply a bigger payment, tax-free after 60, with nothing to sell. Where there is more than one, the one expected to earn the least is drawn first, so the money left invested is the money growing fastest.',
    superannuation: 'Super is tax-free from age 60 and can be drawn without selling anything, so it is used before the shares.',
    shares: 'Shares come last: selling them realises capital gains tax, and each sale has to cover its own tax bill as well as the shortfall.',
};

/** Why a pension started before 1 January 2015 is drawn last (abpPlugin.abpDrawOrder, 22 Oct 2026). */
export const PRE_2015_WHY = 'A pension started before 1 January 2015 is drawn last, whatever it earns: Centrelink still counts its income the older, kinder way, and every dollar taken out of it raises the income counted, while a newer pension drawn instead lowers it. Once emptied, the older treatment is gone for good.';

/**
 * The account's name as the client would say it. A couple's "MLC" and "MLC" (his and hers) become
 * "Colin's MLC" and "Shelly's MLC" from the record's ownership; a joint account or a single client's
 * keeps the plain description. A description that already carries the name is left alone.
 */
function accountName(a: { description?: string; ownership?: string }, key: PoolKey, factFind: DraftFactFind): string {
    const desc = String(a?.description || '').trim() || GROUP_WORDS[key].one.replace(/^the /, '');
    if (factFind.adviceFor !== 'couple') return desc;
    const owner = a?.ownership === 'partner' ? factFind.partner?.firstName : a?.ownership === 'client' ? factFind.client?.firstName : null;
    if (!owner) return desc;
    return desc.toLowerCase().includes(String(owner).toLowerCase()) ? desc : `${owner}'s ${desc}`;
}

export function accountsOf(rows: StoryRow[], factFind: DraftFactFind): Account[] {
    const assets = (factFind.assets || {}) as Record<PoolKey, Array<{ description?: string; ownership?: string; value?: unknown }> | undefined>;
    const out: Account[] = [];
    for (const key of POOL_ORDER) {
        const list = assets[key] || [];
        /*
         * In the order the engine draws them (F.46, 21 Oct 2026): pensions started before 1 January 2015 last
         * (22 Oct 2026), otherwise pensions earning least first, ties in file
         * order (abpPlugin.abpDrawOrder). The index into the projection rows stays the file index.
         */
        const onFile = (a: { growthRate?: unknown }) => !(a?.growthRate == null || a.growthRate === '' || !Number.isFinite(Number(a.growthRate)));
        const rateOf = (a: { growthRate?: unknown }) => (onFile(a) ? Number(a.growthRate) : INVESTMENT_PROFILES.balanced.pensionNetPct);
        const order = list.map((a, i) => ({ a, i }));
        const oldOf = (a: { commencementDateOption?: unknown }) => (a?.commencementDateOption === 'pre-2015' ? 1 : 0);
        if (key === 'accountBasedPensions') order.sort((x, y) => oldOf(x.a as never) - oldOf(y.a as never) || rateOf(x.a as never) - rateOf(y.a as never) || x.i - y.i);
        order.forEach(({ a, i }) => {
            const before = num(a?.value);
            if (before < 1000) return;
            const opening = rows.length ? num(rows[0]?.assets?.[key]?.[i]?.value) : before;
            const lowRow = rows.find((r, ri) => ri > 0 && num(r.assets?.[key]?.[i]?.value) < 1000);
            out.push({
                key,
                name: accountName(a, key, factFind),
                opening,
                before,
                lowYear: lowRow ? Number(lowRow.year) || rows.indexOf(lowRow) + 1 : null,
                spentByPlan: opening < 1000,
                ...(key === 'accountBasedPensions' ? { rate: { pct: rateOf(a as never), source: onFile(a as never) ? 'file' as const : 'assumed' as const }, pre2015: oldOf(a as never) === 1 } : {}),
            });
        });
    }
    return out;
}

export interface MoneyStoryInput {
    rows: StoryRow[];
    factFind: DraftFactFind;
    runway: PlanRunwayCopy | null;
    deposit: { paid: number; refundTo: string };
    /** What the plan itself spent accounts on, for the "not here" line: the deposit (default), the entry payment… */
    spentOn?: string;
    /** The years the plans are judged over ("the 5 years we plan for"). */
    horizonYears: number;
    enteringName: string;
}

export interface MoneyStoryStep {
    from: string;
    title: React.ReactNode;
    why?: string;
    tone: 'money' | 'deposit' | 'home' | 'end';
}

export interface MoneyStory {
    /** One or two sentences for the card. */
    summary: string;
    steps: MoneyStoryStep[];
    /** Accounts the plan spent on the deposit, so they are not in the order. */
    spent: Account[];
    spentOn: string;
    testedYears: number;
    /**
     * Every account in the order it is drawn, each with when it runs low: "ANZ first, which runs low around year 2;
     * then Sally's MLC ABP, which runs low around year 4; then the shares, which last beyond the 5 years tested".
     * For the card's "When the money runs out" line in every module (owner, 22 Oct 2026: a longevity question the
     * client must see, not only in Explain this). Empty where there is no account to draw on.
     */
    orderLine: string;
    /** The sentence after the accounts: the deposit, borrowing, the savings running out, or the home not needed. */
    thenLine: string;
}

/** "expected to earn 5.5% a year after fees (the fund's figure on file)" — the rate behind a pension's place in the order. */
export function rateWords(rate: Account['rate']): string {
    if (!rate) return '';
    const pct = `${Number(rate.pct.toFixed(2))}%`;
    return rate.source === 'file'
        ? `expected to earn ${pct} a year after fees (the figure on the file)`
        : `assumed to earn ${pct} a year after fees (our Balanced assumption — no figure was entered for this fund)`;
}

export function buildMoneyStory({ rows, factFind, runway, deposit, horizonYears, enteringName, spentOn = 'the deposit' }: MoneyStoryInput): MoneyStory | null {
    if (!rows.length) return null;
    const all = accountsOf(rows, factFind);
    const spent = all.filter((a) => a.spentByPlan);
    const live = all.filter((a) => !a.spentByPlan);
    const testedYears = rows.length;
    const steps: MoneyStoryStep[] = [];
    const yearWord = (y: number) => `year ${y}`;
    const pensionsLive = live.filter((a) => a.key === 'accountBasedPensions').length;

    // The accounts, in the engine's order, each with the year it runs low.
    let prevLow: number | null = 1;
    live.forEach((a, i) => {
        const fromYear = i === 0 ? 1 : prevLow;
        // "after the deposit" in aged care; the module's own word elsewhere ("after the entry payment").
        const after = a.opening + 0.5 < a.before ? ` (${formatCurrency(Math.round(a.opening))} after ${spentOn})`: ` (${formatCurrency(Math.round(a.opening))})`;
        const lastsWords = a.lowYear != null ? `runs low around ${yearWord(a.lowYear)}` : `lasts beyond the ${testedYears} years tested`;
        steps.push({
            from: fromYear == null ? 'Then' : i === 0 ? 'From year 1' : `From about ${yearWord(fromYear)}`,
            title: <><strong className="text-slate-900">{a.name}</strong>{after}{a.rate ? `, ${rateWords(a.rate)},` : ''} covers the shortfall and {lastsWords}.</>,
            why: i === 0 || live[i - 1].key !== a.key
                ? `${WHY[a.key]}${a.key === 'accountBasedPensions' && pensionsLive > 1 && live.some((x) => x.pre2015) && live.some((x) => x.key === 'accountBasedPensions' && !x.pre2015) ? ` ${PRE_2015_WHY}` : ''}${a.key === 'accountBasedPensions' && pensionsLive > 1 ? ' The order rests on the rates shown: if a fund is likely to earn more or less than that, the order, and what is left at the end, can change — so check them with the fund.' : ''}`
                : undefined,
            tone: 'money',
        });
        prevLow = a.lowYear;
    });

    if (runway?.radDrawStartsYear != null) {
        steps.push({
            from: `From about ${yearWord(runway.radDrawStartsYear)}`,
            title: <>The care fees are drawn from the <strong className="text-slate-900">refundable deposit itself</strong>. Each dollar drawn reduces what comes back{deposit.refundTo ? ` to ${deposit.refundTo}` : ''} when care ends, and raises the daily room fee.</>,
            tone: 'deposit',
        });
    }
    const homeYear = runway?.borrowingStartsYear ?? null;
    if (homeYear != null) {
        // The rate lives on the runway summary, not the copy type; read it when a caller passed it through.
        const rateVal = (runway as { borrowingRate?: number } | null)?.borrowingRate;
        const rate = rateVal != null ? ` at about ${rateVal}% a year` : '';
        steps.push({
            from: `From about ${yearWord(homeYear)}`,
            title: <><strong className="text-slate-900">This is when the house comes into play.</strong> The plan borrows against the home{rate} (modelled as a reverse mortgage); nothing is repaid until the home is sold, so the debt grows and reduces what is left to the family.</>,
            tone: 'home',
        });
    } else if (runway?.exhaustedYear != null) {
        steps.push({ from: `From about ${yearWord(runway.exhaustedYear)}`, title: <>The savings are gone and nothing accessible is left to cover the shortfall — worth planning another source.</>, tone: 'end' });
    } else {
        steps.push({
            from: 'The house',
            title: <>The home is <strong className="text-slate-900">not needed</strong> within the {horizonYears} {horizonYears === 1 ? 'year' : 'years'} we plan for{testedYears > horizonYears ? `, nor within the ${testedYears} years tested` : ''}.</>,
            tone: 'home',
        });
    }

    // The summary: first account and when it runs low, what carries it next, and the house.
    const first = live[0];
    const second = live[1];
    const parts: string[] = [];
    const radYear = runway?.radDrawStartsYear ?? null;
    if (first) {
        // What carries it after the first account: the next account, or the deposit itself.
        // Nothing after an account that lasts the years tested is drawn, so nothing after it is named — as the card's
        // line (runwayCardLine) says it (live check, 22 Oct 2026: "ANZ … lasts beyond" on the card, "then John's MLC" here).
        const next = first.lowYear == null
            ? `, which lasts beyond the ${testedYears} ${testedYears === 1 ? 'year' : 'years'} tested`
            : second
                ? `; then ${second.name}${live.length > 2 && second.lowYear != null ? ', and so on' : ''}`
                : radYear != null ? `; then the care fees come out of the deposit itself` : '';
        parts.push(`The gap comes out of ${first.name} first${first.lowYear != null ? `, which runs low around year ${first.lowYear}` : ''}${next}.`);
    } else if (radYear != null) {
        parts.push('With no savings account left to draw on, the care fees come out of the deposit itself.');
    } else {
        parts.push('There is no accessible savings account to meet the gap from.');
    }
    // The deposit's turn is said even when borrowing follows it (25 Oct 2026: the card went from the shares straight
    // to "borrows against the home", while the hand-built rail, on the same projection, drew on the deposit first).
    const depositTurn = radYear != null ? `From about year ${radYear} the care fees come out of the deposit itself, which reduces the refund when care ends.` : '';
    if (depositTurn && first && second) parts.push(depositTurn);
    if (homeYear != null) parts.push(`From about year ${homeYear} the plan borrows against the home.`);
    else if (runway?.exhaustedYear != null) parts.push(`The savings are gone around year ${runway.exhaustedYear}.`);
    else parts.push(`The home is not needed within the ${horizonYears} years we plan for.`);
    if (!first && !second) parts.push(`${enteringName}'s pension and other income are what there is.`);
    // Stop at the first account that lasts the years tested: nothing after it is drawn (live check, 22 Oct 2026).
    const lastsAt = live.findIndex((a) => a.lowYear == null);
    const drawn = lastsAt >= 0 ? live.slice(0, lastsAt + 1) : live;
    const orderLine = drawn.map((a, i) => `${i === 0 ? '' : 'then '}${a.name}${i === 0 ? ' first' : ''}, which ${a.lowYear != null ? `runs low around year ${a.lowYear}` : `${/s$/i.test(a.name) && !/'s /.test(a.name) ? 'last' : 'lasts'} beyond the ${testedYears} ${testedYears === 1 ? 'year' : 'years'} tested`}`).join('; ');
    const thenLine = [
        depositTurn,
        homeYear != null
            ? `From about year ${homeYear} the plan borrows against the home.`
            : runway?.exhaustedYear != null ? `The savings are gone around year ${runway.exhaustedYear}.` : '',
    ].filter(Boolean).join(' ');
    return { summary: parts.join(' '), steps, spent, spentOn, testedYears, orderLine, thenLine };
}

const TONE: Record<MoneyStoryStep['tone'], string> = {
    money: 'bg-brand-navy',
    deposit: 'bg-amber-500',
    home: 'bg-brand-navy',
    end: 'bg-rose-500',
};

/** The summary with the full story behind one link — light on first sight, complete on demand. */
export const MoneyStoryBlock = ({ story, testId = 'money-story' }: { story: MoneyStory; testId?: string }) => {
    const [open, setOpen] = useState(false);
    return (
        <span data-testid={testId}>
            {story.summary}{' '}
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-[3px] hover:decoration-brand-navy" data-testid={`${testId}-toggle`}>
                {open ? 'hide the order ‹' : 'see the order, and why ›'}
            </button>
            {open && (
                <ol className="mt-3 space-y-2.5 border-l-2 border-slate-200 pl-4" data-testid={`${testId}-steps`}>
                    {story.steps.map((s, i) => (
                        <li key={i} className="relative">
                            <span className={`absolute -left-[23px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${TONE[s.tone]}`} aria-hidden />
                            <p className="text-[12.5px] font-semibold text-slate-500">{s.from}</p>
                            <p className="text-[14px] leading-relaxed text-slate-700">{s.title}</p>
                            {s.why && <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-500"><span className="font-semibold text-slate-600">Why this order:</span> {s.why}</p>}
                        </li>
                    ))}
                    {story.spent.length > 0 && (
                        <li className="relative">
                            <span className="absolute -left-[23px] top-1.5 h-2.5 w-2.5 rounded-full bg-slate-300 ring-2 ring-white" aria-hidden />
                            <p className="text-[12.5px] leading-relaxed text-slate-500">
                                {story.spent.map((a) => a.name).join(' and ')} went into {story.spentOn}, so {story.spent.length > 1 ? 'they are' : 'it is'} not here{story.spentOn === 'the deposit' ? ' — that money comes back as the refund when care ends' : ''}.
                            </p>
                        </li>
                    )}
                </ol>
            )}
        </span>
    );
};

export default MoneyStoryBlock;

/**
 * THE CARD'S "WHEN THE MONEY RUNS OUT" LINE, ONE FOR EVERY MODULE (owner, 22 Oct 2026: "it would be good to get a
 * breakdown in the same way we do for aged care around when the savings run out … this needs to be consistent in all
 * modules"). No shortfall: income covers each year. A shortfall: every account in the order it is drawn and when
 * each runs low, then the deposit, borrowing or the savings running out. Read off the plan's own projection rows.
 */
export function runwayCardLine(i: { rows: StoryRow[] | null | undefined; factFind: DraftFactFind; horizonYears: number; enteringName?: string; spentOn?: string; deposit?: { paid: number; refundTo: string } }): string | null {
    const rows = (i.rows || []) as StoryRow[];
    const s = summariseFunding(rows as never);
    if (!s.hasData) return null;
    if (!s.hasDeficit) return `it doesn't — income covers the costs in each of the ${s.totalYears ?? rows.length} years tested, so no savings are drawn.`;
    const runway = planRunwayCopy(rows as never, rows.length);
    const story = buildMoneyStory({ rows, factFind: i.factFind, runway, deposit: i.deposit ?? { paid: 0, refundTo: '' }, horizonYears: i.horizonYears, enteringName: i.enteringName || 'they', spentOn: i.spentOn });
    if (!story) return null;
    const lead = (s.firstDeficitYear ?? 1) > 1 ? `income covers the costs until about year ${s.firstDeficitYear}; from then each year's shortfall comes out of ` : `each year's shortfall comes out of `;
    const order = story.orderLine ? `${lead}${story.orderLine}.` : 'there is no savings account to meet the shortfall from.';
    // A shortfall that stops says so, so the line never reads "each year" against a card that is in surplus from
    // then on (25 Oct 2026). Only when every later year is covered.
    const flowOf = (r: StoryRow) => Number((r as { cashFlow?: unknown; netCashflow?: unknown }).cashFlow ?? (r as { netCashflow?: unknown }).netCashflow) || 0;
    const lastShortAt = rows.reduce((last, r, idx) => (flowOf(r) < 0 ? idx : last), -1);
    const coveredAgain = lastShortAt >= 0 && lastShortAt < rows.length - 1
        ? `From about year ${Number((rows[lastShortAt + 1] as { year?: unknown }).year) || lastShortAt + 2} income covers the costs again.`
        : '';
    return [order, story.thenLine, coveredAgain].filter(Boolean).join(' ');
}

/**
 * A PLAN'S ROWS, CUT TO WHAT "EXPLAIN THIS" READS — to save with a chosen plan (owner, 23 Oct 2026: the chosen
 * plan's Explain this was shorter than the tested plan's because the saved card kept no rows). Each year keeps the
 * year, its cashflow, any unfunded shortfall, each liquid account's balance (bank, pensions, super, shares), the
 * borrowing and deposit drawdown lines, the loan's rate, investment income and tax: enough for the money story,
 * the runway line and the investment-income and tax lines, and small enough to save with the file.
 */
export function compactStoryRows(rows: ReadonlyArray<Record<string, unknown>> | null | undefined): Array<Record<string, unknown>> {
    const pools = ['bankAccounts', 'accountBasedPensions', 'superannuation', 'shares'] as const;
    return (rows || []).map((r) => {
        const assets = (r?.assets || {}) as Record<string, Array<{ value?: unknown }> | undefined>;
        const drawdown = ((r?.drawdownBreakdown || []) as Array<{ tag?: string; label?: string }>).filter((d) => d && (d.tag === 'rad' || d.tag === 'borrowing' || /refundable accommodation deposit|borrow|reverse mortgage/i.test(String(d.label || ''))));
        const heloc = (r?.liabilities as { helocDebt?: Array<{ interestRate?: unknown }> } | undefined)?.helocDebt;
        return {
            year: r?.year,
            cashFlow: r?.cashFlow ?? r?.netCashflow,
            ...(Number(r?.unfundedShortfall) > 0 ? { unfundedShortfall: r.unfundedShortfall } : {}),
            assets: Object.fromEntries(pools.map((k) => [k, (assets[k] || []).map((a) => ({ value: Number(a?.value) || 0 }))])),
            ...(drawdown.length ? { drawdownBreakdown: drawdown.map((d) => ({ tag: d.tag, label: d.label })) } : {}),
            ...(heloc && heloc[0]?.interestRate != null ? { liabilities: { helocDebt: [{ interestRate: heloc[0].interestRate }] } } : {}),
            investmentIncome: r?.investmentIncome,
            tax: r?.tax,
        };
    });
}
